import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  CheckBoxDirective,
  SwitchComponent
} from '@progress/kendo-angular-inputs';
import {
  ItemTemplateDirective,
  ListViewComponent
} from '@progress/kendo-angular-listview';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NotificationModuleType } from 'src/app/models/notification/notification-module-type';
import { NotificationStatisticsSliceDto } from 'src/app/models/notification/notification-statistics-dto';
import { NotificationWorkItemDto } from 'src/app/models/notification/notification-work-item-dto';
import { NotificationsService } from 'src/app/services/api/notification-api/notifications.service';
import { NotificationWorkItemNavigationEvent } from '../../model/notification-work-item-navigation-event';
import { NotificationWorkItemWrapper } from '../../model/notification-work-item-wrapper';
import { NotificationsListItemWorkItemComponent } from '../notifications-list-item-work-item/notifications-list-item-work-item.component';

dayjs.extend(utc);

@Component({
  selector: 'app-notifications-list-work-items',
  templateUrl: './notifications-list-work-items.component.html',
  styleUrl: './notifications-list-work-items.component.scss',
  imports: [
    SwitchComponent,
    FormsModule,
    TooltipDirective,
    CheckBoxDirective,
    ListViewComponent,
    ItemTemplateDirective,
    NotificationsListItemWorkItemComponent
  ]
})
export class NotificationsListWorkItemsComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationsService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly statisticsSlice = input<NotificationStatisticsSliceDto>();
  readonly navigate = output<NotificationWorkItemNavigationEvent>();
  readonly statusChanged = output<void>();

  notifications: NotificationWorkItemWrapper[];
  globalSelection: boolean;

  interval: NodeJS.Timeout;

  readonly listView = viewChild.required<ListViewComponent>('listView');

  showOnlyUnread: boolean;

  ngOnInit(): void {
    this.showOnlyUnread = true;
    this.loadNotifications();
    this.interval = setInterval(() => {
      this.loadLatestNotifications();
    }, 10 * 1000);
  }

  private loadNotifications(): void {
    const date = dayjs().subtract(20, 'day');
    this.notificationService
      .getNotifications$(NotificationModuleType.WorkItem, date)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: NotificationWorkItemDto[]) => {
        this.notifications = result.map(n =>
          NotificationWorkItemWrapper.wrap(n)
        );
      });
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  get filteredNotifications(): NotificationWorkItemWrapper[] {
    let result: NotificationWorkItemWrapper[] = [];

    if (this.showOnlyUnread) {
      result = this.notifications?.filter(n => !n.read);
    } else {
      result = this.notifications;
    }
    return result;
  }

  onDelete(notification: NotificationWorkItemWrapper): void {
    this.notificationService
      .delete$([notification.id], notification.moduleType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notifications = this.notifications.filter(
          n => n.id != notification.id
        );
        this.statusChanged.emit();
      });
  }

  deleteSelected(): void {
    const notificationIds = this.notifications
      .filter(n => n.selected)
      .map(n => n.id);
    this.notificationService
      .delete$(notificationIds, NotificationModuleType.WorkItem)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.notifications = this.notifications.filter(
          n => !notificationIds.includes(n.id)
        );
        this.statusChanged.emit();
      });
  }

  onSelectionChanged(): void {
    this.globalSelection = this.notifications?.every(n => n.selected);
  }

  canShowOpenSelected(): boolean {
    const result = this.someSelected() || this.globalSelection;
    return result;
  }

  openSelected(): void {
    const notificationIds = this.notifications
      .filter(n => n.selected)
      .map(n => n.id);
    this.markItemsAsRead$(notificationIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.statusChanged.emit();

          const workItemIds = this.notifications
            .filter(n => n.selected)
            .map(n => n.workItemId);
          const event = new NotificationWorkItemNavigationEvent();
          event.workItemIds = workItemIds;
          this.navigate.emit(event);
        }
      });
  }

  someSelected(): boolean {
    const notifications = this.notifications;
    return (
      !!notifications &&
      notifications.filter(item => item.selected).length > 0 &&
      !this.globalSelection
    );
  }

  globalSelectionChanged(): void {
    this.globalSelection = !this.globalSelection;
    if (this.notifications) {
      this.notifications.forEach(
        item => (item.selected = this.globalSelection)
      );
    }
  }

  openWorkItem(notification: NotificationWorkItemWrapper): void {
    if (notification.read == true) {
      const workItemIds = [notification.workItemId];
      const event = new NotificationWorkItemNavigationEvent();
      event.workItemIds = workItemIds;
      this.navigate.emit(event);
      return;
    }

    const notificationIds = [notification.id];
    this.markItemsAsRead$(notificationIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.statusChanged.emit();

          const workItemIds = [notification.workItemId];
          const event = new NotificationWorkItemNavigationEvent();
          event.workItemIds = workItemIds;
          this.navigate.emit(event);
        }
      });
  }

  markAsRead(notification: NotificationWorkItemWrapper): void {
    const notificationIds = [notification.id];
    this.markItemsAsRead$(notificationIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.statusChanged.emit();
        }
      });
  }

  get unreadNotifications(): number {
    return this.notifications?.filter(x => x.read == false)?.length;
  }

  markAllAsRead(): void {
    this.notificationService
      .markAllAsRead$(NotificationModuleType.WorkItem)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.notifications?.every(n => (n.read = true));
          this.statusChanged.emit();
        }
      });
  }

  loadMore({ sender: _sender }: { sender: ListViewComponent }): void {
    // TODO: use a debounce to avoid loading same data multiple times
  }

  scrollToTop(): void {
    this.listView().focus(0);
  }

  private markItemsAsRead$(notificationIds: string[]): Observable<boolean> {
    return this.notificationService
      .markItemsAsRead$(NotificationModuleType.WorkItem, notificationIds)
      .pipe(
        tap((result: boolean) => {
          if (result) {
            for (const notification of this.notifications) {
              if (notificationIds.some(id => id == notification.id)) {
                notification.read = !notification.read;
              }
            }
          }
        })
      );
  }

  private loadLatestNotifications(): void {
    const latestNotification =
      this.notifications?.length > 0
        ? this.notifications.reduce((a, b) =>
            a.createdTime > b.createdTime ? a : b
          )
        : null;
    const latestDate =
      latestNotification != null
        ? dayjs(latestNotification.createdTime).utc()
        : dayjs().utc().subtract(1, 'hour');

    this.notificationService
      .getNotifications$(NotificationModuleType.WorkItem, latestDate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: NotificationWorkItemDto[]) => {
        const currentNotificationsIds =
          this.notifications?.map(x => x.id) ?? [];

        const notificationsToAdd = result.filter(
          (notification: NotificationWorkItemDto) => {
            return currentNotificationsIds?.indexOf(notification.id) == -1;
          }
        );

        if (notificationsToAdd?.length == 0) {
          return;
        }

        const notificationsToAddWrappers = notificationsToAdd.map(n =>
          NotificationWorkItemWrapper.wrap(n)
        );

        this.notifications = [
          ...notificationsToAddWrappers,
          ...(this.notifications ?? [])
        ];
      });
  }
}
